export type WalletStatus =
  | "idle"
  | "pending"
  | "connected"
  | "unavailable"
  | "rejected"
  | "error";

export interface WalletState {
  status: WalletStatus;
  address: string | null;
  chainId: string | null;
  message: string | null;
}

interface EthereumProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
}

declare global {
  interface Window {
    ethereum?: EthereumProvider;
    reviewWallet?: WalletState;
  }
}

export const IDLE_WALLET: WalletState = {
  status: "idle",
  address: null,
  chainId: null,
  message: null,
};

export function getEthereumProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  const provider = window.ethereum;
  if (!provider || typeof provider.request !== "function") return null;
  return provider;
}

/** Face starea portofelului vizibilă pentru codul din review/index.html. */
export function publishWalletState(state: WalletState): void {
  if (typeof window === "undefined") return;
  window.reviewWallet = state;
  window.dispatchEvent(new CustomEvent("reviewwalletchange", { detail: state }));
}

export function shortenAddress(address: string | null): string {
  if (!address) return "";
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

function readErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    const candidate = error as { message?: unknown };
    if (typeof candidate.message === "string" && candidate.message.trim()) {
      return candidate.message;
    }
  }
  return "Unknown wallet error.";
}

function readErrorCode(error: unknown): number | null {
  if (error && typeof error === "object") {
    const candidate = error as { code?: unknown };
    if (typeof candidate.code === "number") return candidate.code;
  }
  return null;
}

/**
 * Cere conectarea portofelului. Apelul `eth_requestAccounts` pleacă
 * sincron, în același click, ca fereastra portofelului să apară imediat.
 */
export async function connectWallet(): Promise<WalletState> {
  const provider = getEthereumProvider();

  if (!provider) {
    return {
      status: "unavailable",
      address: null,
      chainId: null,
      message:
        "window.ethereum is not available. Install MetaMask or open the site in a supported browser.",
    };
  }

  try {
    const accounts = await provider.request({
      method: "eth_requestAccounts",
    });
    const list = Array.isArray(accounts) ? (accounts as string[]) : [];
    const address = list.length > 0 && typeof list[0] === "string" ? list[0] : null;

    if (!address) {
      return {
        status: "rejected",
        address: null,
        chainId: null,
        message: "No account was shared by the wallet.",
      };
    }

    let chainId: string | null = null;
    try {
      const rawChainId = await provider.request({ method: "eth_chainId" });
      chainId = typeof rawChainId === "string" ? rawChainId : null;
    } catch {
      chainId = null;
    }

    return {
      status: "connected",
      address,
      chainId,
      message: null,
    };
  } catch (error) {
    const code = readErrorCode(error);
    return {
      status: code === 4001 ? "rejected" : "error",
      address: null,
      chainId: null,
      message:
        code === 4001
          ? "The connection request was rejected in the wallet."
          : readErrorMessage(error),
    };
  }
}

export function walletLabel(state: WalletState): string {
  switch (state.status) {
    case "pending":
      return "Connecting…";
    case "connected":
      return shortenAddress(state.address);
    case "unavailable":
      return "No wallet";
    case "rejected":
      return "Cancelled";
    case "error":
      return "Failed";
    default:
      return "";
  }
}